import React, { FC, useMemo } from 'react'
import get from 'lodash/get'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentCharacteristics, IShipmentStop, IShipmentStopTypes } from 'types/graphqlTypes'
import {
  BouncedIcon,
  DropTrailerIcon,
  HighValueIcon,
  HotIcon,
  RolledIcon,
  BackhaulIcon,
  MustGoIcon,
  ContractFilledIcon,
  PreferredIcon,
  HazmatIcon,
  BackupOutlinedIcon,
  SameDaySpotIcon,
  TrueRatePlusFilledIcon,
  QrCodeScanIcon,
} from 'transfix-icons'

export interface IShipmentIconsProps {
  icons: string[]
  bouncedCount?: number
  rolledCount?: number
  stops?: Pick<IShipmentStop, 'dropTrailer' | 'stopSequence' | 'stopType'>[]
  driverVerifiedText?: string
}

interface IIcons {
  [key: string]: {
    tooltip?: string | JSX.Element
    Component: FC<any>
    className: string
  }
}

interface IIconMap {
  [key: string]: string
}

const ICON_MAP: IIconMap = {
  backhaulEligible: 'backhaul',
  bounceCount: 'bounced',
  fraudValidation: 'fraudValidation',
  hasDropTrailerStops: 'trailer',
  hot: 'hot',
  mustGo: 'mustGo',
  rolloverCount: 'rolled',
  pricedUnderContract: 'contract',
  contractBackup: 'contractBackup',
  sameDaySpot: 'sameDaySpot',
  trueRatePlus: 'trueRatePlus',
  cargoValue: 'cargoValue',
}

type IIconCharacteristics = IShipmentCharacteristics & {
  pricedUnderContract: boolean
  contractBackup: boolean
  sameDaySpot: boolean
  cargoValue: boolean
}

export const getIcons = (
  characteristics?: Partial<Omit<IIconCharacteristics, 'hazmat'>>
): string[] => {
  const icons: string[] = []
  Object.keys(characteristics || {}).reduce((acum, current) => {
    const currentCharacteristic = get(characteristics, current, null)
    if (currentCharacteristic) {
      acum.push(ICON_MAP[current])
    }

    return acum
  }, icons)
  return icons
}

const getStopsWithDrops = (
  stops: Pick<IShipmentStop, 'dropTrailer' | 'stopSequence' | 'stopType'>[]
): string => {
  return stops
    .filter(({ dropTrailer }) => dropTrailer)
    .map(({ stopSequence, stopType }) =>
      stopType === IShipmentStopTypes.pickup ? `P${stopSequence}` : `D${stopSequence}`
    )
    .join(', ')
}

const useStyles = makeStyles(theme => ({
  iconsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    height: '1.25rem',
    width: '1.25rem',
  },
  coolGray5Color: {
    color: brandColors.coolGray5,
  },
  infoColor: {
    color: brandColors.info,
  },
  errorColor: {
    color: brandColors.error,
  },
  error1: {
    color: brandColors.error1,
  },
}))

export const ShipmentIcons: FC<IShipmentIconsProps> = ({
  bouncedCount,
  icons,
  rolledCount,
  stops = [],
  driverVerifiedText,
}) => {
  const classes = useStyles()

  const stopsWithDrops = getStopsWithDrops(stops)

  const ICONS: IIcons = useMemo(
    () => ({
      bounced: {
        tooltip: `Bounced ${bouncedCount}x`,
        Component: BouncedIcon,
        className: `${classes.icon} ${classes.infoColor}`,
      },
      hot: {
        tooltip: 'Hot',
        Component: HotIcon,
        className: classes.icon,
      },
      rolled: {
        tooltip: `Rolled ${rolledCount}x`,
        Component: RolledIcon,
        className: `${classes.icon} ${classes.errorColor}`,
      },
      backhaul: {
        tooltip: 'Backhaul Eligible',
        Component: BackhaulIcon,
        className: classes.icon,
      },
      mustGo: {
        Component: MustGoIcon,
        className: `${classes.icon} ${classes.errorColor}`,
        tooltip: 'Must Go',
      },
      contract: {
        tooltip: 'Contract',
        Component: ContractFilledIcon,
        className: `${classes.icon} ${classes.coolGray5Color}`,
      },
      contractBackup: {
        tooltip: 'Contract Backup',
        Component: BackupOutlinedIcon,
        className: `${classes.icon} ${classes.coolGray5Color}`,
      },
      preferred: {
        tooltip: 'Preferred Customer',
        Component: PreferredIcon,
        className: classes.icon,
      },
      hazmat: {
        tooltip: 'Hazmat',
        Component: HazmatIcon,
        className: classes.icon,
      },
      sameDaySpot: {
        tooltip: 'Same Day Spot',
        Component: SameDaySpotIcon,
        className: `${classes.icon} ${classes.coolGray5Color}`,
      },
      trailer: {
        tooltip: `Drop Trailer${stopsWithDrops ? ` (${stopsWithDrops})` : ``}`,
        Component: DropTrailerIcon,
        className: `${classes.icon} ${classes.coolGray5Color}`,
      },
      trueRatePlus: {
        tooltip: 'TrueRate+',
        Component: TrueRatePlusFilledIcon,
        className: `${classes.icon} ${classes.coolGray5Color}`,
      },
      cargoValue: {
        tooltip: 'High Value - Over $100k',
        Component: HighValueIcon,
        className: classes.icon,
      },
      fraudValidation: {
        tooltip: (
          <>
            Check-in verification code
            <br />
            {driverVerifiedText}
          </>
        ),
        Component: QrCodeScanIcon,
        className: `${classes.icon} ${classes.error1}`,
      },
    }),
    [bouncedCount, classes, rolledCount, stopsWithDrops, driverVerifiedText]
  )

  return (
    <div className={classes.iconsContainer}>
      {icons.map(currentIcon => {
        const { Component, className, tooltip } = ICONS[currentIcon] || {}

        if (!Component) {
          return null
        }

        return tooltip ? (
          <div key={currentIcon} data-test={`shipment-icon-tooltip-${Component.displayName}`}>
            <Tooltip
              position='bottom'
              id={currentIcon}
              trigger={
                <div className={classes.iconWrapper}>
                  <Component className={className} />
                </div>
              }>
              {tooltip}
            </Tooltip>
          </div>
        ) : (
          <div
            key={currentIcon}
            className={classes.iconWrapper}
            data-test={`shipment-icon-${Component.displayName}`}>
            <Component className={className} />
          </div>
        )
      })}
    </div>
  )
}

export default ShipmentIcons
